/**
 * Whether Player AI can answer here at all, read once when the panel opens
 * (module visibility, decision d6).
 *
 * Player AI has no on/off switch of its own. What makes it unusable is a
 * workspace with no AI model, and before this a person found that out only by
 * sending a message and reading a Run failure. The panel now asks the app
 * profile read (`GET /settings/apps/{app}/profile`) up front and says so.
 *
 * Copy is reused from the failure catalogue wherever it already says the right
 * thing, so the panel and a failed Run never word the same fact two ways.
 */

import {
  describePlayerAIFailure,
  playerAIFailureCode,
  type PlayerAIFailure,
} from "./failures.js";
import type {
  AIAppId,
  AppProfileConfiguration,
  PlayerAIScope,
} from "./types.js";

export type PlayerAIReadiness =
  | { readonly state: "ready" }
  /** No AI model is available to this person: an administrator must add one. */
  | { readonly state: "not_set_up"; readonly failure: PlayerAIFailure }
  /** The read itself failed; the copy says how to recover. */
  | { readonly state: "failed"; readonly failure: PlayerAIFailure };

/** The d6 title, said exactly so. */
export const PLAYER_AI_NOT_SET_UP_TITLE = "Player AI isn’t set up yet.";

/**
 * The profile app a scope reads. Only four exist: LAMA, KPI Manager and every
 * other page with no workspace of its own use `home`, and a Hoshin workshop
 * uses its plan's `hoshin`.
 */
export function playerAIAppIdForScope(scope: PlayerAIScope | null): AIAppId {
  if (!scope || !("workspaceType" in scope)) return "home";
  switch (scope.workspaceType) {
    case "solve":
      return "solve";
    case "vsm":
      return "vsm";
    case "hoshin":
    case "hoshin_workshop":
      return "hoshin";
  }
}

/**
 * A successful read: no profile at all means nothing can answer. An answer
 * that carries no list (an older Player AI, a stub) proves nothing either way,
 * so the panel says nothing rather than claiming Player AI isn't set up.
 */
export function playerAIReadinessFromProfile(
  configuration: AppProfileConfiguration | undefined,
): PlayerAIReadiness {
  const profiles = configuration?.profiles;
  if (!Array.isArray(profiles) || profiles.length > 0) {
    return { state: "ready" };
  }
  const failure = describePlayerAIFailure({
    source: "read",
    code: "AI_NOT_CONFIGURED",
  });
  return {
    state: "not_set_up",
    failure: { ...failure, title: PLAYER_AI_NOT_SET_UP_TITLE },
  };
}

/**
 * A failed read. An outage keeps the catalogue's temporary copy. A refusal
 * here means the delegated session can no longer be exchanged — the read
 * authorizes nothing else — so it is an expired session, and signing in again
 * is the one recovery.
 */
export function playerAIReadinessFromError(error: unknown): PlayerAIReadiness {
  const { code, ...context } = playerAIFailureCode(error);
  return {
    state: "failed",
    failure: describePlayerAIFailure({
      source: "read",
      code: SESSION_REFUSALS.has(code) ? "AUTH_SESSION_INVALID" : code,
      ...context,
    }),
  };
}

const SESSION_REFUSALS: ReadonlySet<string> = new Set([
  "FORBIDDEN",
  "UNAUTHENTICATED",
  "AUTH_SESSION_INVALID",
]);
